// Watercolor sprite loader. Sprites live in public/assets/ (optimized copies of the
// originals in repo-root assets/) and are served at <base>assets/*.png. Everything degrades
// to the prototype's vector drawings if an image is missing or still loading — so the game
// is always playable (CLAUDE.md §2 integration rules).
import type { Prio } from '../game/types';

export type SpriteKey =
  | `ticket-${Prio}`
  | `ticket-${Prio}-left`
  | `ticket-${Prio}-right`
  | 'customer-angry'
  | 'customer-happy'
  | 'parachute'
  | 'dupes-full';

const PRIOS: Prio[] = ['P0', 'P1', 'P2', 'P3', 'P4', 'P5'];

// Filenames are lowercase kebab-case; ticket keys map P0->p0.
function fileFor(key: SpriteKey): string {
  return key.replace(/P(\d)/g, 'p$1') + '.png';
}

const MANIFEST: SpriteKey[] = [
  ...PRIOS.flatMap((p) => [`ticket-${p}`, `ticket-${p}-left`, `ticket-${p}-right`] as SpriteKey[]),
  'customer-angry',
  'customer-happy',
  'parachute',
  'dupes-full',
];

const images = new Map<SpriteKey, HTMLImageElement>();
const ready = new Set<SpriteKey>();

// Returns the decoded image only once it has loaded successfully, else null (→ vector fallback).
export function getSprite(key: SpriteKey): HTMLImageElement | null {
  return ready.has(key) ? images.get(key)! : null;
}

// The list of URLs to precache in the service worker (step 4).
export function spriteUrls(): string[] {
  return MANIFEST.map((k) => import.meta.env.BASE_URL + 'assets/' + fileFor(k));
}

// Kick off loading of every sprite. Resolves when all have settled (loaded or failed) so a
// missing file never blocks the game. Idempotent.
let started = false;
export function preloadSprites(): Promise<void> {
  if (started) return Promise.resolve();
  started = true;
  return new Promise((resolve) => {
    let pending = MANIFEST.length;
    if (!pending) return resolve();
    const done = () => {
      if (--pending === 0) resolve();
    };
    for (const key of MANIFEST) {
      const img = new Image();
      images.set(key, img);
      img.onload = () => {
        ready.add(key);
        done();
      };
      img.onerror = () => done(); // leave it out of `ready` → fallback to vector
      img.src = import.meta.env.BASE_URL + 'assets/' + fileFor(key);
    }
  });
}
